import { AppointmentOrderItem, Order } from '@spike/order-model';
import Pet from '@spike/pet-model';
import { Task } from '@spike/task-model';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { PetTagTabs } from 'components/UI/V3/PetTag/Tabs/PetTagTabs';
import { TaskCard } from 'components/Tasks/Detail/TaskCard';
import TaskComponent from 'components/Tasks/Task/TaskComponent';
import Button from 'components/UI/V3/Button/Button';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { TasksStatus } from '@spike/tasks-action';
import { reloadOrderThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import styles from './OrderDetailFeedingTab.module.scss';

interface OrderDetailFeedingTabProps {
    order?: Order;
    pets: Array<Pet>;
    className?: string;
    readOnly?: boolean;
}

const FEEDING_MOMENT_ORDER = [1, 2, 3] as const;

interface DayGroup {
    dateKey: string;
    date: Moment;
    moments: Map<number, Task[]>;
}

const findAppointmentItem = (appointmentItems: Array<AppointmentOrderItem>, task: Task) =>
    appointmentItems.find(item => item.appointment.id === task.appointmentTaskId);

interface FeedingDaySectionProps {
    dayGroup: DayGroup;
    expanded: boolean;
    onToggle: () => void;
    appointmentItems: Array<AppointmentOrderItem>;
    allFeedingTasks: Task[];
    readOnly: boolean;
    onEdit: (task: Task) => void;
}

const FeedingDaySection: React.FC<FeedingDaySectionProps> = ({
    dayGroup,
    expanded,
    onToggle,
    appointmentItems,
    allFeedingTasks,
    readOnly,
    onEdit
}) => {
    return (
        <div className={styles.daySection}>
            <button
                type="button"
                className={styles.dayHeader}
                onClick={onToggle}
                aria-expanded={expanded}
            >
                <span>{dayGroup.date.format('dddd, DD MMM YYYY')}</span>
                <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    className={styles.dayHeaderChevron}
                />
            </button>

            {expanded && (
                <div className={styles.dayMoments}>
                    {FEEDING_MOMENT_ORDER.map(momentId => {
                        const tasks = dayGroup.moments.get(momentId) ?? [];

                        return tasks.map(task => {
                            const appointmentItem = findAppointmentItem(appointmentItems, task);

                            if (!appointmentItem) {
                                return null;
                            }

                            return (
                                <TaskCard
                                    key={task.id ?? task.uuid}
                                    task={task}
                                    tasks={allFeedingTasks}
                                    appointmentItem={appointmentItem}
                                    readOnly={readOnly}
                                    onEdit={onEdit}
                                />
                            );
                        });
                    })}
                </div>
            )}
        </div>
    );
};

export const OrderDetailFeedingTab: React.FC<OrderDetailFeedingTabProps> = ({ order, pets, className, readOnly }) => {
    const timezone = useTimeZone();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const tasksStatus = useSelector<RootState, TasksStatus>(state => state.tasks.status);
    const [selectedPet, setSelectedPet] = useState<Pet>(pets[0]);
    const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
    const [showTaskComponent, setShowTaskComponent] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined);
    const [taskModalKey, setTaskModalKey] = useState(0);
    const isReadOnly = readOnly ?? false;

    useNonInitialEffect(() => {
        switch (tasksStatus) {
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
            case TasksStatus.Error:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
        }
    }, [tasksStatus]);

    const appointmentItems = order?.items?.appointments ?? [];

    const feedingTasksByPet = useMemo(() => {
        const tasksMap = new Map<number, Task[]>();

        order?.items?.appointments?.forEach(item => {
            if (item.appointment) {
                const petId = item.appointment.pet.id;
                const feedingTasks = (item.appointment.tasks || []).filter(
                    task => task.feeding?.feedingMomentTypeId !== undefined
                );

                if (feedingTasks.length === 0) {
                    return;
                }

                if (!tasksMap.has(petId)) {
                    tasksMap.set(petId, []);
                }
                tasksMap.get(petId)!.push(...feedingTasks);
            }
        });

        return tasksMap;
    }, [order]);

    const selectedPetFeedingTasks = useMemo(() => {
        return feedingTasksByPet.get(selectedPet.id!) || [];
    }, [feedingTasksByPet, selectedPet]);

    const dayGroups = useMemo((): DayGroup[] => {
        const groups = new Map<string, DayGroup>();

        selectedPetFeedingTasks.forEach(task => {
            if (!task.taskDate) {
                return;
            }

            const date = (moment.isMoment(task.taskDate) ? task.taskDate.clone() : moment(task.taskDate))
                .clone()
                .tz(timezone)
                .startOf('day');
            const dateKey = date.format('YYYY-MM-DD');

            if (!groups.has(dateKey)) {
                groups.set(dateKey, {
                    dateKey,
                    date,
                    moments: new Map()
                });
            }

            const group = groups.get(dateKey)!;
            const momentId = task.feeding?.feedingMomentTypeId ?? 3;

            if (!group.moments.has(momentId)) {
                group.moments.set(momentId, []);
            }

            group.moments.get(momentId)!.push(task);
        });

        return Array.from(groups.values()).sort((left, right) => left.date.valueOf() - right.date.valueOf());
    }, [selectedPetFeedingTasks, timezone]);

    useEffect(() => {
        setExpandedDays(new Set(dayGroups.map(group => group.dateKey)));
    }, [dayGroups]);

    useEffect(() => {
        if (pets.length > 0 && !pets.some(pet => pet.id === selectedPet.id)) {
            setSelectedPet(pets[0]);
        }
    }, [pets, selectedPet.id]);

    const toggleDay = (dateKey: string) => {
        setExpandedDays(prev => {
            const next = new Set(prev);

            if (next.has(dateKey)) {
                next.delete(dateKey);
            } else {
                next.add(dateKey);
            }

            return next;
        });
    };

    const handlerCloseTaskComponent = () => {
        setShowTaskComponent(false);
        setTaskToEdit(undefined);
    };

    const handlerAddTaskFromSuccess = () => {
        setTaskToEdit(undefined);
        setTaskModalKey(prev => prev + 1);
    };

    const handlerEditTask = (task: Task) => {
        if (isReadOnly) return;
        setTaskToEdit(task);
        setTaskModalKey(prev => prev + 1);
        setShowTaskComponent(true);
    };

    const handlerAddFeeding = () => {
        if (isReadOnly) return;
        setTaskToEdit(undefined);
        setTaskModalKey(prev => prev + 1);
        setShowTaskComponent(true);
    };

    const hasFeedingTasks = selectedPetFeedingTasks.length > 0;

    return (
        <div className={clsx(styles.root, className)}>
            <PetTagTabs
                pets={pets}
                value={selectedPet}
                onChange={setSelectedPet}
                className={styles.tabs}
            />

            {!hasFeedingTasks && <p className={styles.emptyStateText}>There are no feeding for this pet.</p>}

            <Button
                label="+ Add Feeding"
                variant="green"
                className={styles.addTaskButton}
                disabled={isReadOnly}
                onClick={handlerAddFeeding}
            />

            {hasFeedingTasks && (
                <div className={styles.daysList}>
                    {dayGroups.map(dayGroup => (
                        <FeedingDaySection
                            key={dayGroup.dateKey}
                            dayGroup={dayGroup}
                            expanded={expandedDays.has(dayGroup.dateKey)}
                            onToggle={() => toggleDay(dayGroup.dateKey)}
                            appointmentItems={appointmentItems}
                            allFeedingTasks={selectedPetFeedingTasks}
                            readOnly={isReadOnly}
                            onEdit={task => {
                                if (isReadOnly) return;
                                handlerEditTask(task);
                            }}
                        />
                    ))}
                </div>
            )}

            {showTaskComponent && (
                <TaskComponent
                    key={taskModalKey}
                    petId={selectedPet.id!}
                    task={taskToEdit}
                    appointments={order?.items?.appointments || []}
                    defaultTaskType={taskToEdit ? undefined : 'feeding'}
                    onClose={handlerCloseTaskComponent}
                    onAddTask={handlerAddTaskFromSuccess}
                />
            )}
        </div>
    );
};

export default OrderDetailFeedingTab;
